import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Megaphone, Play, Pause, Upload, Users } from 'lucide-react';
import type { Campaign } from '@conversia/shared-types';
import * as campaignsApi from '@/api/campaigns.api';
import * as channelsApi from '@/api/channels.api';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { PageHeader } from '@/components/common/PageHeader';
import { campaignStatusBadge } from '@/lib/badges';
import { relativeTime } from '@/lib/format';
import { useToast } from '@/hooks/useToast';

const createSchema = z.object({
  name: z.string().min(1, 'El nombre es obligatorio'),
  channelId: z.string().optional(),
});
type CreateValues = z.infer<typeof createSchema>;

function CreateCampaignModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { data: channels } = useQuery({ queryKey: ['channels', 'list'], queryFn: () => channelsApi.listChannels() });

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<CreateValues>({ resolver: zodResolver(createSchema), defaultValues: { name: '', channelId: '' } });

  useEffect(() => {
    if (open) reset({ name: '', channelId: '' });
  }, [open, reset]);

  const create = useMutation({
    mutationFn: (values: CreateValues) =>
      campaignsApi.createCampaign({ name: values.name, channelId: values.channelId || undefined }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['campaigns'] });
      toast.success('Campaña creada');
      onClose();
    },
    onError: (err) =>
      setError('root', { message: err instanceof ApiError ? err.message : 'No se pudo crear' }),
  });

  const onSubmit = handleSubmit((values) => create.mutate(values));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Nueva campaña"
      description="Crea una campaña en borrador y luego carga sus destinatarios."
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={onSubmit} loading={create.isPending}>
            Crear campaña
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <Input label="Nombre" placeholder="Promo de verano" error={errors.name?.message} {...register('name')} />
        <Select label="Canal (opcional)" {...register('channelId')}>
          <option value="">Sin canal</option>
          {channels?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        {errors.root && <p className="text-sm text-danger">{errors.root.message}</p>}
      </form>
    </Modal>
  );
}

function RecipientsModal({ campaign, onClose }: { campaign: Campaign | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [csv, setCsv] = useState('');

  useEffect(() => {
    setCsv('');
  }, [campaign]);

  const importRecipients = useMutation({
    mutationFn: () => campaignsApi.addRecipients(campaign!.id, csv),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ['campaigns'] });
      toast.success('Destinatarios cargados', `${res.added} agregados · ${res.skipped} omitidos`);
      onClose();
    },
    onError: () => toast.error('No se pudieron cargar los destinatarios'),
  });

  return (
    <Modal
      open={Boolean(campaign)}
      onClose={onClose}
      size="lg"
      title="Cargar destinatarios"
      description="Pega un CSV con columnas como phone,name. La primera fila son los encabezados."
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={() => importRecipients.mutate()} loading={importRecipients.isPending} disabled={!csv.trim()}>
            <Upload className="h-4 w-4" /> Importar
          </Button>
        </>
      }
    >
      <Textarea
        rows={10}
        value={csv}
        onChange={(e) => setCsv(e.target.value)}
        placeholder={'phone,name\n18095551234,Juan\n18095555678,María'}
        className="font-mono text-xs"
      />
    </Modal>
  );
}

export default function Campaigns() {
  const qc = useQueryClient();
  const toast = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [recipientsFor, setRecipientsFor] = useState<Campaign | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['campaigns', 'list'],
    queryFn: () => campaignsApi.listCampaigns({ take: 100 }),
  });

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'RUNNING' | 'PAUSED' }) =>
      campaignsApi.setStatus(id, status),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['campaigns'] });
    },
    onError: () => toast.error('No se pudo cambiar el estado'),
  });

  const items = data?.items ?? [];

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Campañas"
        helpKey="campaigns"
        description="Envíos masivos con ritmo controlado por canal."
        action={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" /> Nueva campaña
          </Button>
        }
      />

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner size={28} />
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Megaphone}
          title="Sin campañas"
          description="Crea tu primera campaña para enviar mensajes a una lista de contactos."
          action={
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" /> Crear campaña
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {items.map((c) => {
            const badge = campaignStatusBadge(c.status);
            const stats = c.statsJson ?? {};
            const isRunning = c.status === 'RUNNING';
            return (
              <Card key={c.id}>
                <CardBody className="flex flex-col gap-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300">
                        <Megaphone className="h-5 w-5" />
                      </span>
                      <div className="min-w-0">
                        <h3 className="truncate font-semibold text-slate-900 dark:text-slate-100">{c.name}</h3>
                        <p className="text-xs text-slate-400">{relativeTime(c.createdAt)}</p>
                      </div>
                    </div>
                    <Badge variant={badge.variant}>{badge.label}</Badge>
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-center">
                    <Stat label="Total" value={stats.total ?? 0} />
                    <Stat label="Enviados" value={stats.sent ?? 0} />
                    <Stat label="Respondidos" value={stats.replied ?? 0} />
                  </div>

                  <div className="flex items-center gap-2 border-t border-surface-border pt-3 dark:border-slate-800">
                    <Button variant="outline" size="sm" onClick={() => setRecipientsFor(c)}>
                      <Users className="h-4 w-4" /> Destinatarios
                    </Button>
                    {isRunning ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setStatus.mutate({ id: c.id, status: 'PAUSED' })}
                        loading={setStatus.isPending && setStatus.variables?.id === c.id}
                      >
                        <Pause className="h-4 w-4" /> Pausar
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => setStatus.mutate({ id: c.id, status: 'RUNNING' })}
                        loading={setStatus.isPending && setStatus.variables?.id === c.id}
                      >
                        <Play className="h-4 w-4" /> Iniciar
                      </Button>
                    )}
                  </div>
                </CardBody>
              </Card>
            );
          })}
        </div>
      )}

      <CreateCampaignModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <RecipientsModal campaign={recipientsFor} onClose={() => setRecipientsFor(null)} />
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md bg-surface-muted py-2 dark:bg-slate-800/50">
      <p className="text-base font-bold text-slate-900 dark:text-slate-100">{value}</p>
      <p className="text-[11px] text-slate-400">{label}</p>
    </div>
  );
}
